/**
 * AtlasProvenanceModule — the visual lineage browser.
 *
 * Opens an overlay rendering the derivative tree for any asset: it walks UP to
 * the root original (parentAssetId chain), then renders the full subtree of
 * derivatives (remixes + template instances) DOWN from the root, highlighting
 * the asset you clicked. This is the GitHub-like lineage of the World Remix
 * Chain made visible. The tree is built purely from the public `asset` rows the
 * client already caches — no extra authority.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { Asset } from '@shared/types/inventory.js';

interface TreeNode {
  asset: Asset;
  children: TreeNode[];
}

export class AtlasProvenanceModule implements IModule {
  readonly name = 'AtlasProvenance';

  private overlay!: HTMLElement;
  private treeEl!: HTMLElement;
  private subEl!: HTMLElement;

  init(_app: App): void {
    this.overlay = document.getElementById('lineage')!;
    this.treeEl = document.getElementById('lineage-tree')!;
    this.subEl = document.getElementById('lineage-sub')!;
    (document.getElementById('lineage-close') as HTMLButtonElement).addEventListener('click', () => this.close());

    bus.on('atlas:show-lineage', ({ assetId }) => this.open(assetId));
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('show')) this.close();
    });
  }

  private open(assetId: bigint): void {
    const focus = atlas.assets.get(assetId.toString());
    if (!focus) return;

    // Walk up to the root original.
    let root = focus;
    const seen = new Set<string>();
    while (root.parentAssetId !== 0n && !seen.has(root.assetId.toString())) {
      seen.add(root.assetId.toString());
      const parent = atlas.assets.get(root.parentAssetId.toString());
      if (!parent) break;
      root = parent;
    }

    const tree = this.buildSubtree(root);
    this.subEl.textContent =
      `Root #${root.assetId} by ${root.creator.slice(0, 8)}… · viewing lineage of #${assetId}`;
    this.treeEl.innerHTML = '';
    this.renderNode(tree, 0, assetId, true);
    this.overlay.classList.add('show');
  }

  /** Build the full derivative subtree rooted at `asset`. */
  private buildSubtree(asset: Asset, depth = 0): TreeNode {
    const node: TreeNode = { asset, children: [] };
    if (depth > 64) return node; // cycle guard
    for (const a of atlas.assets.values()) {
      if (a.parentAssetId === asset.assetId) {
        node.children.push(this.buildSubtree(a, depth + 1));
      }
    }
    node.children.sort((x, y) => Number(x.asset.assetId - y.asset.assetId));
    return node;
  }

  private renderNode(node: TreeNode, depth: number, focusId: bigint, isRoot: boolean): void {
    const a = node.asset;
    const indent = depth === 0 ? '' : '  '.repeat(depth - 1) + (isRoot ? '' : '└─ ');
    const tag = isRoot && depth === 0 ? '● ' : '◦ ';
    const line = document.createElement('div');
    const isFocus = a.assetId === focusId;
    const premium = a.isPremium ? ' ★' : '';
    line.innerHTML =
      `<span class="node">${indent}${tag}</span>` +
      `<span class="${isFocus ? 'self' : 'node'}">#${a.assetId} ${a.kind}</span>` +
      `<span class="premium">${premium}</span>` +
      `<span class="creator"> · ${a.creator.slice(0, 8)}…</span>`;
    this.treeEl.appendChild(line);
    for (const child of node.children) {
      this.renderNode(child, depth + 1, focusId, false);
    }
  }

  private close(): void {
    this.overlay.classList.remove('show');
  }
}
